import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import styled from 'styled-components';
import {
  StyledAlertDialogContent,
  StyledAlertDialogOverlay,
} from '@/features/auth-managment/styles';
import {
  BodyTextRegularTertiary,
  BoldHeadingQuinary,
  HeadingRegularSecondary,
} from '@/shared/ui/typography';
import { Image } from '@/shared/ui/image';
import { useScopedI18n } from '@locales/client';

const Wrapper = styled.div`
  background: #161639;
  padding: 16px;
  max-width: 720px;
`;

const Title = styled.div`
  color: #edeff5;

  font-size: 50px;
  padding-top: 64px;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  width: 60%;
  float: right;
  padding-right: 24px;
  text-shadow:
    0px 4px 4px rgba(0, 0, 0, 0.25),
    0px 4px 4px rgba(0, 0, 0, 0.25);

  @media (max-width: 768px) {
    font-size: 32px;
    padding-top: 40px;
    width: 50%;
    margin: 0 auto;
  }
`;

const Banner = styled.div`
  height: 375px;
  width: 720px;
  position: relative;
  background-image: url('/images/background/banner_cashback.jpg');
  background-size: contain;
  background-repeat: no-repeat;

  @media (max-width: 768px) {
    height: 210px;
    width: 100%;
    background-size: cover;
  }
`;

export const Blocks = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Block = styled.div`
  display: flex;
  flex-basis: 340px;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    flex-basis: 100%;
  }
`;

export const BlockHeader = styled.div`
  background: rgba(65, 77, 107, 0.6);
  padding: 24px 0;
  text-align: center;
  border-top-right-radius: 8px;
  border-top-left-radius: 8px;
`;

export const BlockContent = styled.div`
  padding: 14px 8px;
  background: rgba(65, 77, 107, 0.2);
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  min-height: 185px;
`;

export const CloseIcon = styled(Image)`
  position: absolute;
  right: 12px;
  top: 12px;
  cursor: pointer;
`;

const CashbackModal = ({ visible, onClose }) => {
  const t = useScopedI18n('weekly_cashback');
  return (
    <Dialog.Root open={visible} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <StyledAlertDialogOverlay />
        <StyledAlertDialogContent>
          <Banner>
            <Title>{t('title')}</Title>
            <div onClick={() => onClose()}>
              <CloseIcon src="/images/icons/close-cashback.svg" />
            </div>
          </Banner>
          <Wrapper>
            <Blocks>
              <Block>
                <div>
                  <BlockHeader>
                    <BoldHeadingQuinary>
                      {t('description.what_is_cashback.title')}
                    </BoldHeadingQuinary>
                  </BlockHeader>
                  <BlockContent>
                    <BodyTextRegularTertiary>
                      {t('description.what_is_cashback.content')}
                    </BodyTextRegularTertiary>
                  </BlockContent>
                </div>
              </Block>
              <Block>
                <div>
                  <BlockHeader>
                    <BoldHeadingQuinary>
                      {t('description.important_information.title')}
                    </BoldHeadingQuinary>
                  </BlockHeader>
                  <BlockContent>
                    <BodyTextRegularTertiary>
                      <ul style={{ padding: '8px 16px' }}>
                        <li>
                          {t(
                            'description.important_information.content.cashback_activation_period'
                          )}
                        </li>
                        <li>
                          {t(
                            'description.important_information.content.cashback_credited_as_bonus'
                          )}
                        </li>
                        <li>
                          {t(
                            'description.important_information.content.deposits_with_bonus_exclusion'
                          )}
                        </li>
                        <li>
                          {t(
                            'description.important_information.content.wager_requirement'
                          )}
                        </li>
                      </ul>
                    </BodyTextRegularTertiary>
                  </BlockContent>
                </div>
              </Block>
              <Block>
                <div>
                  <BlockHeader>
                    <BoldHeadingQuinary>
                      {t('description.how_to_activate_cashback.title')}
                    </BoldHeadingQuinary>
                  </BlockHeader>
                  <BlockContent>
                    <BodyTextRegularTertiary>
                      {t('description.how_to_activate_cashback.content')}
                    </BodyTextRegularTertiary>
                  </BlockContent>
                </div>
              </Block>
              <Block>
                <div>
                  <BlockHeader>
                    <BoldHeadingQuinary>
                      {t('description.conditions_for_receiving_cashback.title')}
                    </BoldHeadingQuinary>
                  </BlockHeader>
                  <BlockContent>
                    <BodyTextRegularTertiary>
                      <ul style={{ padding: '8px 16px' }}>
                        <li>
                          {t(
                            'description.conditions_for_receiving_cashback.requirements.email_verification'
                          )}
                        </li>
                        <li>
                          {t(
                            'description.conditions_for_receiving_cashback.requirements.phone_verification'
                          )}
                        </li>
                        <li>
                          {t(
                            'description.conditions_for_receiving_cashback.requirements.min_deposit_amount'
                          )}
                        </li>
                      </ul>
                    </BodyTextRegularTertiary>
                  </BlockContent>
                </div>
              </Block>
            </Blocks>
          </Wrapper>
        </StyledAlertDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export { CashbackModal };
